import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, Dialog, Stack, TextField, Typography
} from "@mui/material";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PhonelinkLockIcon from "@mui/icons-material/PhonelinkLock";
import {
  setReauthHandler, setSessionLostHandler, setPasskeyRequiredHandler
} from "../../authClient";
import { reauthWithPasskey, reauthWithPassword } from "../../auth/passkeys";
import { COLORS } from "../Home/constants";
import { ghostButtonSx, primaryButtonSx, inputSx } from "../Home/styles";
import { PhoneCodePanel } from "./PhoneCodePanel";

// ─────────────────────────────────────────────────────────────────────────────
// AuthBridge — connects authClient.js to the React app
//
//   • session lost        → /login
//   • passkey_required    → /recover (a recovery session must create its passkey)
//   • reauth_required     → the "Confirm it's you" dialog below; the request
//                           that needed it is retried once it succeeds.
//
// Mounted once, inside the router (App.js).
// ─────────────────────────────────────────────────────────────────────────────

/** A computer (mouse, hover), not a phone or tablet. */
const onComputer = () => typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

export default function AuthBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    setSessionLostHandler(() => navigate("/login", { replace: true }));
    setPasskeyRequiredHandler(() => {
      if (!window.location.pathname.startsWith("/recover")) navigate("/recover", { replace: true });
    });
    return () => { setSessionLostHandler(null); setPasskeyRequiredHandler(null); };
  }, [navigate]);

  return <ReauthDialog />;
}

/**
 * "Confirm it's you." Opened by authClient when the server answers
 * 403 reauth_required; resolves true after the server accepted a check.
 * A passkey needs a click to start (Safari only allows it from a tap), which is
 * why this is a dialog with a button rather than an automatic prompt.
 */
function ReauthDialog() {
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState(["passkey"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [viaPhone, setViaPhone] = useState(false);   // "Confirm with your phone" (no passkey on this computer)
  const [windowMinutes, setWindowMinutes] = useState(5);
  const resolver = useRef(null);

  useEffect(() => {
    setReauthHandler((m, info = {}) => new Promise((resolve) => {
      resolver.current = resolve;
      setMethods(Array.isArray(m) && m.length ? m : ["passkey"]);
      setWindowMinutes(Number(info.windowMinutes) > 0 ? Number(info.windowMinutes) : 5);
      setError("");
      setPassword("");
      setBusy(false);
      // The server lists the phone first for a computer signed in from the phone
      // (it most likely has no passkey of its own): open straight on the phone,
      // with the passkey as a link. Phones themselves always use their passkey.
      setViaPhone(Array.isArray(m) && m[0] === "phone" && (m.length === 1 || onComputer()));
      setOpen(true);
    }));
    return () => setReauthHandler(null);
  }, []);

  const finish = (ok) => {
    setOpen(false);
    const r = resolver.current;
    resolver.current = null;
    if (r) r(ok);
  };

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      finish(true);
    } catch (err) {
      setBusy(false);
      if (!err?.cancelled) setError(err?.message || "That didn't work. Please try again.");
    }
  };

  const usePasskey = methods.includes("passkey");
  const usePassword = !usePasskey && methods.includes("password");
  const googleOnly = !usePasskey && !usePassword && methods.includes("google");
  // A phone confirms itself with its own passkey; the offer is for computers.
  const phoneOffered = methods.includes("phone") && onComputer();
  // "5 minutes" for account security, "12 hours" for daily work.
  const lasts = windowMinutes >= 60 ? `${Math.round(windowMinutes / 60)} hours` : `${windowMinutes} minutes`;
  const covers = windowMinutes >= 60 ? "Stream keys and reporter setup" : "Security settings";

  return (
    <Dialog
      open={open}
      onClose={() => !busy && finish(false)}
      aria-labelledby="reauth-title"
      PaperProps={{ sx: {
        width: "100%", maxWidth: viaPhone ? 460 : 420, m: 2, p: { xs: 3, sm: 3.5 },
        bgcolor: COLORS.raised, backgroundImage: "none", color: COLORS.text,
        border: `1px solid ${COLORS.borderStrong}`, borderRadius: "22px",
        boxShadow: "0 32px 80px rgba(0,0,0,0.55)", fontFamily: COLORS.fontSans
      } }}
      sx={{ "& .MuiBackdrop-root": { bgcolor: "rgba(8,7,24,0.72)" } }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
          bgcolor: "rgba(43,189,175,0.14)", color: COLORS.cyan,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          {viaPhone ? <PhonelinkLockIcon /> : <FingerprintIcon />}
        </Box>
        <Box minWidth={0}>
          <Typography id="reauth-title" sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 700, fontSize: 20, lineHeight: 1.25 }}>
            Confirm it's you
          </Typography>
          <Typography sx={{ mt: 0.75, color: COLORS.textSoft, fontSize: 14, lineHeight: 1.55 }}>
            {viaPhone && `Approve this computer on the phone where you're signed in to Fennira. It counts for ${lasts} here.`}
            {!viaPhone && usePasskey && `${covers} need a quick check with your passkey. It counts for ${lasts} on this device.`}
            {!viaPhone && usePassword && "Enter your password to continue. Adding a passkey replaces this step with a fingerprint or face check."}
            {!viaPhone && googleOnly && "Sign in with Google again to continue, then repeat what you were doing."}
          </Typography>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: "12px" }}>{error}</Alert>}

      {viaPhone && (
        <Box sx={{ mt: 2.5 }}>
          <PhoneCodePanel mode="reauth" tone="dark" compact onApproved={() => finish(true)} />
        </Box>
      )}

      {!viaPhone && usePassword && (
        <TextField
          autoFocus fullWidth size="small" type="password" label="Password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy}
          onKeyDown={(e) => { if (e.key === "Enter" && password) run(() => reauthWithPassword(password)); }}
          sx={{ ...inputSx, mt: 2.5, "& .MuiInputLabel-root": { color: COLORS.muted } }}
        />
      )}

      {!viaPhone && phoneOffered && (
        <Button onClick={() => { setError(""); setViaPhone(true); }} disabled={busy} startIcon={<PhonelinkLockIcon />}
          sx={{ mt: 2.5, color: COLORS.cyan, textTransform: "none", fontWeight: 600, px: 1 }}>
          No passkey on this computer? Confirm with your phone
        </Button>
      )}

      <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mt: viaPhone || phoneOffered ? 1.5 : 3 }}>
        {viaPhone && (usePasskey || usePassword || googleOnly) && (
          <Button onClick={() => { setError(""); setViaPhone(false); }}
            sx={{ mr: "auto", color: COLORS.muted, textTransform: "none", fontWeight: 600, px: 1 }}>
            {usePasskey ? "Use a passkey instead" : usePassword ? "Use my password instead" : "Use Google instead"}
          </Button>
        )}
        <Button onClick={() => finish(false)} disabled={busy} sx={ghostButtonSx}>Cancel</Button>
        {!viaPhone && usePasskey && (
          <Button autoFocus onClick={() => run(reauthWithPasskey)} disabled={busy}
            startIcon={busy ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <FingerprintIcon />}
            sx={primaryButtonSx}>
            Use passkey
          </Button>
        )}
        {!viaPhone && usePassword && (
          <Button onClick={() => run(() => reauthWithPassword(password))} disabled={busy || !password} sx={primaryButtonSx}>
            Confirm
          </Button>
        )}
        {!viaPhone && googleOnly && (
          <Button onClick={() => { finish(false); window.location.assign("/auth/google"); }} sx={primaryButtonSx}>
            Sign in with Google
          </Button>
        )}
      </Stack>
    </Dialog>
  );
}
